import React from 'react';
import SpinnerIcon from './SpinnerIcon';

interface SpinnerProps {
  variant?: 'overlay' | 'inline' | 'centered';
  size?: number;
  message?: string;
  showIcon?: boolean;
  className?: string;
  messageClassName?: string;
}

/**
 * Versatile spinner component for various loading scenarios
 * Supports overlay, inline, and centered variants
 * Follows Next.js best practices with proper accessibility
 */
const Spinner: React.FC<SpinnerProps> = ({
  variant = 'centered',
  size = 6,
  message = 'loading...',
  showIcon = true,
  className = '',
  messageClassName = '',
}) => {
  const baseSpinnerClasses = 'flex items-center justify-center';

  const variantClasses = {
    overlay: 'fixed inset-0 bg-black/60 backdrop-blur-sm z-50',
    inline: 'py-4',
    centered: 'min-h-[200px]',
  };

  const textClasses = {
    overlay: 'text-foreground-muted',
    inline: 'text-foreground-muted',
    centered: 'text-foreground-muted',
  };

  const iconClasses = {
    overlay: 'text-white',
    inline: 'text-foreground-muted',
    centered: 'text-foreground-muted',
  };

  return (
    <div
      className={`${baseSpinnerClasses} ${variantClasses[variant]} ${className}`}
      role="status"
      aria-live="polite"
    >
      <div className="flex items-center justify-center gap-2">
        {showIcon && <SpinnerIcon size={size} className={iconClasses[variant]} />}
        {message && (
          <div
            className={`animate-pulse text-sm font-medium ${textClasses[variant]} ${messageClassName}`}
            aria-label={message}
          >
            {message}
          </div>
        )}
      </div>
    </div>
  );
};

Spinner.displayName = 'Spinner';

export default Spinner;

